import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { z } from 'zod'
import { currencies, type Preferences } from '_/features/settings/domain/preferences'

export const themeSchema = z.enum(['system', 'light', 'dark'])
export const currencySchema = z.enum(currencies)
const preferencesSchema = z.object({
	theme: themeSchema.catch('dark'),
	currency: currencySchema.catch('BRL'),
	liveRates: z.boolean().catch(true),
})

type PreferencesStore = Preferences & {
	update: (preferences: Partial<Preferences>) => void
}

export const usePreferences = create<PreferencesStore>()(
	persist(set => ({ theme: 'dark', currency: 'BRL', liveRates: true, update: preferences => set(preferences) }), {
		name: 'nina-preferences',
		storage: createJSONStorage(() => localStorage),
		partialize: ({ theme, currency, liveRates }) => ({ theme, currency, liveRates }),
		merge: (persisted, current) => {
			const parsed = preferencesSchema.safeParse(persisted)
			return parsed.success ? { ...current, ...parsed.data } : current
		},
	}),
)

export const initializeTheme = () => {
	const media = window.matchMedia('(prefers-color-scheme: dark)')
	const apply = () => {
		const { theme } = usePreferences.getState()
		const dark = theme === 'dark' || (theme === 'system' && media.matches)
		document.documentElement.classList.toggle('dark', dark)
		document.documentElement.classList.toggle('light', !dark)
		document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#1a1a1d' : '#f5f5f2')
	}
	apply()
	const unsubscribe = usePreferences.subscribe(apply)
	media.addEventListener('change', apply)
	return () => {
		unsubscribe()
		media.removeEventListener('change', apply)
	}
}
